import { format } from "date-fns"
import { CalendarIcon } from "lucide-react"

import {
  Button,
  Calendar,
  cn,
  Field,
  FieldError,
  FieldLabel,
  Popover,
  PopoverContent,
  PopoverTrigger,
} from "@atlant/ui"

interface FormDatePickerProps {
  value: Date | undefined
  onChange: (date: Date | undefined) => void
  name?: string
  label?: string
  placeholder?: string
  disabled?: boolean
  className?: string
  error?: string
}

export const FormDatePicker = ({
  value,
  onChange,
  name,
  label,
  placeholder = "Pick a date",
  disabled,
  className,
  error,
}: FormDatePickerProps) => {
  const id = name ?? label?.toLowerCase().replace(/\s+/g, "-")

  return (
    <Field className={className} data-invalid={error ? true : undefined}>
      {label && (
        <FieldLabel htmlFor={id} className="text-muted-foreground text-sm font-medium">
          {label}
        </FieldLabel>
      )}
      <Popover>
        <PopoverTrigger asChild>
          <Button
            id={id}
            type="button"
            variant="outline"
            disabled={disabled}
            className={cn(
              "border-input hover:bg-input/10 h-10 w-full justify-between gap-2 px-3 text-left font-normal",
              !value && "text-muted-foreground",
              error && "border-destructive dark:border-destructive",
            )}
          >
            <span className="truncate">
              {value ? (
                format(value, "dd MMM yyyy")
              ) : (
                <span className="text-placeholder">{placeholder}</span>
              )}
            </span>
            <CalendarIcon className="text-muted-foreground/80 size-4 shrink-0" />
          </Button>
        </PopoverTrigger>
        <PopoverContent className="w-auto p-0" align="start" sideOffset={4}>
          <Calendar mode="single" selected={value} onSelect={onChange} />
        </PopoverContent>
      </Popover>
      {error && <FieldError>{error}</FieldError>}
    </Field>
  )
}
